import Button from '@components/Button'
import ErrorPopup from '@components/ErrorPopup'
import Picker from '@components/Picker'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { logger } from '@plug-core/logger'
import { useCurrentWallet, useUserTokens, useCustomTokens } from '@plug-core/state'
import { useTranslation } from 'react-i18next'
import { validateCanisterId } from '@plug-core/utils/validationHelper'
import useConfig from '@plug-core/hooks/useConfig'
import { Stack } from '@mui/material'
import getAgent from '@plug-core/utils/getAgent'
import React, { FC, useCallback, useMemo, useState } from 'react'
import getTokenMetadata from '@plug-core/utils/tokenActions/getMetadata'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import type { TokenDetails } from '@plug-core/interfaces/common'

const TOKEN_STANDARDS = [
  { key: 'ICRC2', humanValue: 'ICRC-2' },
  { key: 'ICRC1', humanValue: 'ICRC-1' },
  { key: 'DIP20', humanValue: 'DIP20' },
  { key: 'DRC20', humanValue: 'DRC20' },
]

interface Props {
  hideForm: () => void
  customSaveAction?: (token: TokenDetails) => void
}

const CustomTokenForm: FC<Props> = ({ hideForm, customSaveAction }) => {
  const [canisterAddress, setCanisterAddress] = useState('')
  const [standard, setStandard] = useState('')
  const [addError, setAddError] = useState<string>()
  const [metadataLoading, setMetadataLoading] = useState(false)

  const { currentWallet } = useCurrentWallet()
  const customTokens = useCustomTokens()
  const userTokens = useUserTokens()
  const { tokens } = useConfig()
  const { t } = useTranslation()
  const { fireEvent } = useGoogleAnalytics()

  const validPrincipal = useMemo(() => {
    if (!canisterAddress) return

    return validateCanisterId(canisterAddress)
  }, [canisterAddress])

  const agent = useMemo(() => {
    return getAgent()
  }, [])

  const onCustomTokenAdd = useCallback(() => {
    if (tokens.find((token) => token.address === canisterAddress)) {
      setAddError('This token already exists')
      return
    }

    setMetadataLoading(true)

    getTokenMetadata(canisterAddress, standard, agent)
      .then((tokenDetails) => {
        if (customSaveAction) {
          customSaveAction(tokenDetails)
        } else {
          customTokens.addToken(currentWallet, tokenDetails)
          userTokens.addToken(currentWallet, tokenDetails.principalId)
        }

        fireEvent(GAEventName.CUSTOM_TOKEN_ADDED, {
          standard,
          address: tokenDetails.principalId,
          name: tokenDetails.name,
        })

        hideForm()
      })
      .catch((e) => {
        logger.error('Unable to fetch token metadata', { originalError: e })
        setAddError(
          'Unable to fetch token metadata. Please check that you have selected correct standard.'
        )
      })
      .finally(() => setMetadataLoading(false))
  }, [canisterAddress, standard, agent, customTokens.addToken, userTokens.addToken, currentWallet])

  return (
    <>
      <Stack position="fixed" top={0} zIndex={2}>
        <ErrorPopup
          visible={!!addError}
          title="Token add Error"
          description={addError}
          onDismiss={() => setAddError(undefined)}
        />
      </Stack>

      <Stack p={2} spacing={2}>
        <Titlebar title={t('manageTokens.addCustom')} />
        <Stack spacing={1}>
          <RainbowTextField
            error={validPrincipal === false}
            value={canisterAddress}
            onChange={(e) => setCanisterAddress(e.target.value)}
            placeholder="Canister ID"
          />
          <Picker
            values={TOKEN_STANDARDS}
            currentValue={standard}
            onValueChange={setStandard}
            heading="Select Standard"
          />
        </Stack>
        <Button
          variant="rainbow"
          disabled={!validPrincipal || !standard}
          fullWidth
          onClick={onCustomTokenAdd}
          loading={metadataLoading}
        >
          {t('manageTokens.addCustom')}
        </Button>
      </Stack>
    </>
  )
}

export default CustomTokenForm
